import React, { useEffect, useState } from "react";
import { contactMessagesService, ContactMessage } from "@/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Mail,
  Search,
  RefreshCw,
  MoreVertical,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  Archive,
  Trash2,
  Send,
  MessageSquare,
  Download,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { SidebarToggle, useAdminLayout } from "@/components/admin/AdminLayout";

const AdminMessages = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<ContactMessage[]>(
    []
  );
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [adminNotes, setAdminNotes] = useState("");
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [processingSending, setProcessingSending] = useState(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const { toast } = useToast();
  const { isMobile } = useAdminLayout();

  const itemsPerPage = 10;

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredMessages(messages);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = messages.filter(
        (msg) =>
          msg.email.toLowerCase().includes(query) ||
          msg.name.toLowerCase().includes(query) ||
          msg.subject.toLowerCase().includes(query) ||
          msg.message.toLowerCase().includes(query)
      );
      setFilteredMessages(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, messages]);

  const fetchMessages = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await contactMessagesService.getAll();
      setMessages(data);
      setFilteredMessages(data);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Không thể tải danh sách tin nhắn. Vui lòng thử lại sau.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (
    id: string,
    status: "read" | "replied" | "archived"
  ) => {
    setProcessingAction(id);
    try {
      const result = await contactMessagesService.updateStatus(id, status);

      if (result.success) {
        // Update local state
        const updatedMessages = messages.map((msg) =>
          msg.id === id ? { ...msg, status } : msg
        );
        setMessages(updatedMessages);

        toast({
          title: "Cập nhật thành công",
          description: `Trạng thái tin nhắn đã được cập nhật.`,
          duration: 3000,
        });
      } else {
        toast({
          title: "Cập nhật không thành công",
          description: result.error || "Đã xảy ra lỗi khi cập nhật trạng thái.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (err) {
      console.error("Error updating message status:", err);
      toast({
        title: "Cập nhật không thành công",
        description: "Đã xảy ra lỗi khi cập nhật trạng thái.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setProcessingAction(null);
    }
  };

  const handleMarkAsReplied = async (id: string) => {
    setProcessingAction(id);
    try {
      const result = await contactMessagesService.markAsReplied(id);

      if (result.success) {
        // Update local state
        const updatedMessages = messages.map((msg) =>
          msg.id === id
            ? {
                ...msg,
                status: "replied",
                reply_sent_at: new Date().toISOString(),
              }
            : msg
        );
        setMessages(updatedMessages);

        toast({
          title: "Đánh dấu đã trả lời",
          description: "Tin nhắn đã được đánh dấu là đã trả lời.",
          duration: 3000,
        });
      } else {
        toast({
          title: "Cập nhật không thành công",
          description: result.error || "Đã xảy ra lỗi khi cập nhật trạng thái.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (err) {
      console.error("Error marking message as replied:", err);
      toast({
        title: "Cập nhật không thành công",
        description: "Đã xảy ra lỗi khi cập nhật trạng thái.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setProcessingAction(null);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedMessage) return;

    setProcessingSending(true);
    try {
      const result = await contactMessagesService.addNotes(
        selectedMessage.id,
        adminNotes
      );

      if (result.success) {
        // Update local state
        const updatedMessages = messages.map((msg) =>
          msg.id === selectedMessage.id
            ? { ...msg, admin_notes: adminNotes }
            : msg
        );
        setMessages(updatedMessages);

        toast({
          title: "Ghi chú đã được lưu",
          description: "Ghi chú nội bộ đã được cập nhật thành công.",
          duration: 3000,
        });

        setIsNotesDialogOpen(false);
      } else {
        toast({
          title: "Không thể lưu ghi chú",
          description: result.error || "Đã xảy ra lỗi khi lưu ghi chú.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (err) {
      console.error("Error saving notes:", err);
      toast({
        title: "Không thể lưu ghi chú",
        description: "Đã xảy ra lỗi khi lưu ghi chú. Vui lòng thử lại sau.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setProcessingSending(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Bạn có chắc chắn muốn xóa tin nhắn này không?")) {
      return;
    }

    setProcessingAction(id);
    try {
      const result = await contactMessagesService.delete(id);

      if (result.success) {
        // Update local state
        const updatedMessages = messages.filter((msg) => msg.id !== id);
        setMessages(updatedMessages);

        toast({
          title: "Xóa thành công",
          description: "Tin nhắn đã được xóa khỏi hệ thống.",
          duration: 3000,
        });
      } else {
        toast({
          title: "Không thể xóa tin nhắn",
          description: result.error || "Đã xảy ra lỗi khi xóa tin nhắn.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (err) {
      console.error("Error deleting message:", err);
      toast({
        title: "Không thể xóa tin nhắn",
        description: "Đã xảy ra lỗi khi xóa tin nhắn. Vui lòng thử lại sau.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setProcessingAction(null);
    }
  };

  const openViewDialog = (message: ContactMessage) => {
    // If status is new, automatically mark as read
    if (message.status === "new") {
      handleUpdateStatus(message.id, "read");
    }

    setSelectedMessage(message);
    setIsViewDialogOpen(true);
  };

  const openNotesDialog = (message: ContactMessage) => {
    setSelectedMessage(message);
    setAdminNotes(message.admin_notes || "");
    setIsNotesDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <Badge className="bg-blue-500">Mới</Badge>;
      case "read":
        return <Badge className="bg-purple-500">Đã đọc</Badge>;
      case "replied":
        return <Badge className="bg-green-500">Đã trả lời</Badge>;
      case "archived":
        return <Badge className="bg-gray-500">Đã lưu trữ</Badge>;
      default:
        return <Badge className="bg-gray-400">{status}</Badge>;
    }
  };

  // Calculate pagination
  const pageCount = Math.ceil(filteredMessages.length / itemsPerPage);
  const paginatedMessages = filteredMessages.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          {isMobile && <SidebarToggle className="mr-2" />}
          <h1 className="text-xl md:text-2xl font-semibold">
            Tin nhắn liên hệ
          </h1>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchMessages}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          <span>Làm mới</span>
        </Button>
      </div>

      {/* Search and filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Tìm kiếm tin nhắn theo tên, email, tiêu đề hoặc nội dung..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Lỗi</h3>
              <div className="text-sm text-red-700 mt-2">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Messages table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Trạng thái</TableHead>
                  <TableHead>Người gửi</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tiêu đề</TableHead>
                  <TableHead className="w-[130px]">Ngày gửi</TableHead>
                  <TableHead className="w-[100px] text-right">
                    Thao tác
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      <div className="flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Đang tải tin nhắn...
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedMessages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      <div className="flex flex-col items-center justify-center">
                        <MessageSquare className="h-10 w-10 text-muted-foreground/40 mb-2" />
                        <p className="text-muted-foreground">
                          {searchQuery
                            ? "Không tìm thấy tin nhắn phù hợp"
                            : "Chưa có tin nhắn nào"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMessages.map((message) => (
                    <TableRow
                      key={message.id}
                      className={
                        message.status === "new"
                          ? "bg-blue-50/40 font-medium"
                          : ""
                      }
                    >
                      <TableCell>{getStatusBadge(message.status)}</TableCell>
                      <TableCell className="font-medium">
                        {message.name}
                      </TableCell>
                      <TableCell>{message.email}</TableCell>
                      <TableCell>
                        <span className="line-clamp-1">{message.subject}</span>
                      </TableCell>
                      <TableCell>
                        {formatDate(new Date(message.created_at))}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              disabled={processingAction === message.id}
                            >
                              {processingAction === message.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <MoreVertical className="h-4 w-4" />
                              )}
                              <span className="sr-only">Menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openViewDialog(message)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              <span>Xem chi tiết</span>
                            </DropdownMenuItem>

                            {message.status !== "replied" && (
                              <DropdownMenuItem
                                onClick={() => handleMarkAsReplied(message.id)}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Đánh dấu đã trả lời</span>
                              </DropdownMenuItem>
                            )}

                            {message.status !== "archived" && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleUpdateStatus(message.id, "archived")
                                }
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                <span>Lưu trữ</span>
                              </DropdownMenuItem>
                            )}

                            <DropdownMenuItem
                              onClick={() => openNotesDialog(message)}
                            >
                              <MessageSquare className="h-4 w-4 mr-2" />
                              <span>Thêm ghi chú</span>
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDelete(message.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              <span>Xóa</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && filteredMessages.length > 0 && (
        <div className="mt-4 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  className={
                    currentPage === 1 ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>

              {Array.from({ length: pageCount }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    onClick={() => setCurrentPage(i + 1)}
                    isActive={currentPage === i + 1}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(pageCount, prev + 1))
                  }
                  className={
                    currentPage === pageCount
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* View Message Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedMessage && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {selectedMessage.subject}
                </DialogTitle>
                <DialogDescription className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">Từ: </span>
                    <span>
                      {selectedMessage.name} ({selectedMessage.email})
                    </span>
                  </div>
                  <div>{formatDate(new Date(selectedMessage.created_at))}</div>
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4">
                <div className="bg-gray-50 p-4 rounded-md whitespace-pre-line">
                  {selectedMessage.message}
                </div>

                {selectedMessage.admin_notes && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold mb-1">
                      Ghi chú nội bộ:
                    </h4>
                    <div className="bg-yellow-50 p-3 rounded-md text-sm">
                      {selectedMessage.admin_notes}
                    </div>
                  </div>
                )}

                {selectedMessage.reply_sent_at && (
                  <div className="mt-4 flex items-center text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span>
                      Đã trả lời vào{" "}
                      {formatDate(new Date(selectedMessage.reply_sent_at))}
                    </span>
                  </div>
                )}
              </div>

              <DialogFooter className="flex gap-2 flex-wrap sm:flex-nowrap justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setIsViewDialogOpen(false)}
                >
                  Đóng
                </Button>

                <Button
                  variant="outline"
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    openNotesDialog(selectedMessage);
                  }}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Thêm ghi chú
                </Button>

                <Button
                  onClick={() => {
                    // Create mailto link
                    const mailtoLink = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`;
                    window.open(mailtoLink);

                    // Ask if they want to mark as replied
                    if (
                      confirm(
                        "Bạn có muốn đánh dấu tin nhắn này là đã trả lời không?"
                      )
                    ) {
                      handleMarkAsReplied(selectedMessage.id);
                    }

                    setIsViewDialogOpen(false);
                  }}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Trả lời qua Email
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={isNotesDialogOpen} onOpenChange={setIsNotesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Thêm ghi chú nội bộ</DialogTitle>
            <DialogDescription>
              Ghi chú này chỉ hiển thị cho admin và không được gửi cho người
              dùng.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <Textarea
              placeholder="Nhập ghi chú nội bộ về tin nhắn này..."
              className="min-h-[150px]"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsNotesDialogOpen(false)}
            >
              Hủy
            </Button>
            <Button onClick={handleSaveNotes} disabled={processingSending}>
              {processingSending ? (
                <span className="flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  <span>Đang lưu...</span>
                </span>
              ) : (
                <span>Lưu ghi chú</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;
